/**
 * Knowledge: read and find what you know. One search across your notes, your teams' shared notes and your tasks
 * (closed ones too), browse by folder / company / team / tag, read a note with related ones, and Ask — Fusion AI
 * answers from your own notes and tasks and shows which ones it used. Editing stays in Notes. When FusionDocs is on,
 * its matches show in their own section under ONE's results.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import DOMPurify from "dompurify";
import { BookOpen, CheckSquare, FileText, Folder, Pencil, Search, Sparkles, Tag, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, post } from "../lib/api";
import { Card, Empty, ErrorNote, fmtDate, PageHeader } from "../components/ui";
import { useCategories } from "../components/Categories";
import { SubProgress } from "../components/work";
import { FusionDocsHits, useFusionDocs, type FusionDocsResult } from "../components/FusionDocs";

interface Hit { kind: "note" | "task"; id: string; title: string; folder: string | null; tags: string[] | null; company: string | null; team: string | null; owner: string | null; at: string; snippet: string | null; ref: string | null; state: string | null; pinned: boolean; subtasks?: { total: number; done: number } | null }
interface Facets { folders: Array<{ folder: string; n: number }>; companies: Array<{ code: string; name: string; n: number }>; tags: Array<{ tag: string; n: number }>; teams: Array<{ id: string; name: string; n: number }>; total: number }
interface NoteView { note: { id: string; title: string; body: string; folder: string; tags: string[]; company: string | null; team: string | null; owner: string; updated_at: string; canEdit: boolean }; related: Array<{ id: string; title: string; folder: string }>; task: { id: string; ref: string; title: string; state: string } | null }
interface Answer { answer: string | null; note?: string; sources: Array<{ n: number; kind: "note" | "task"; id: string; title: string; ref: string | null; cited: boolean }>;
  fusionDocs?: { answer: string; found: boolean; sources: Array<{ n: number; title: string; url?: string; cited?: boolean }> } | null }
type Filter = { folder?: string; tag?: string; company?: string; team?: string; teamName?: string; category?: string; categoryName?: string };

/** «match» from the server → <mark>; everything else is plain text. */
function Snippet({ text }: { text: string | null }) {
  if (!text) return null;
  const parts = text.replace(/\s+/g, " ").split(/(«[^»]*»)/g);
  return <span>{parts.map((p, i) => (p.startsWith("«") ? <mark key={i} className="rounded bg-amber-100 px-0.5 text-inherit">{p.slice(1, -1)}</mark> : <span key={i}>{p}</span>))}</span>;
}

function useDebounced<T>(v: T, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => { const h = setTimeout(() => setD(v), ms); return () => clearTimeout(h); }, [v, ms]);
  return d;
}

function Reader({ id, onOpen, onClose }: { id: string; onOpen: (id: string) => void; onClose: () => void }) {
  const q = useQuery({ queryKey: ["knowledge-note", id], queryFn: () => api<NoteView>(`/api/knowledge/notes/${id}`) });
  if (!q.data) return <Card><Empty>{q.isLoading ? "Opening…" : "Note not found."}</Empty></Card>;
  const { note: n, related, task } = q.data;
  return (
    <Card flush>
      <div className="flex items-start gap-2 border-b border-[var(--line)] px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold leading-snug">{n.title || "Untitled"}</h2>
          <p className="muted mt-1 text-xs">
            {[n.folder || "Unfiled", n.company, n.team ? `Shared with ${n.team}` : "Private", `${n.owner} · ${fmtDate(n.updated_at)}`].filter(Boolean).join(" · ")}
          </p>
          {n.tags.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{n.tags.map((t) => <span key={t} className="chip text-xs">#{t}</span>)}</div>}
        </div>
        <a href={`/notes?note=${n.id}`} className="btn btn-sm shrink-0"><Pencil size={14} /> {n.canEdit ? "Edit" : "Open in Notes"}</a>
        <button className="btn btn-ghost btn-sm !px-1.5" onClick={onClose} aria-label="Close"><X size={15} /></button>
      </div>
      <div className="prose-note px-5 py-4 text-[0.9375rem] leading-relaxed [&_a]:text-brand-600 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_h2]:mt-3 [&_h2]:text-lg [&_h2]:font-bold [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(n.body || "<p class='muted'>Empty note.</p>") }} />
      {(task || related.length > 0) && (
        <div className="space-y-3 border-t border-[var(--line)] px-5 py-4 text-sm">
          {task && <p><CheckSquare size={14} className="muted mr-1 inline" />Task: <Link to="/work/$id" params={{ id: task.id }} className="font-medium text-brand-700 hover:underline">{task.ref} {task.title}</Link></p>}
          {related.length > 0 && (
            <div>
              <h3 className="section-label mb-1">Related</h3>
              <ul className="space-y-1">{related.map((r) => <li key={r.id}><button className="text-left hover:underline" onClick={() => onOpen(r.id)}><FileText size={13} className="muted mr-1 inline" />{r.title || "Untitled"}{r.folder && <span className="muted text-xs"> · {r.folder}</span>}</button></li>)}</ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Ask() {
  const [question, setQuestion] = useState("");
  const ask = useMutation({ mutationFn: (q: string) => post<Answer>("/api/knowledge/ask", { question: q }) });
  const a = ask.data;
  const render = (text: string) => text.split(/(\[\d+\])/g).map((p, i) => {
    const m = p.match(/^\[(\d+)\]$/);
    const s = m ? a?.sources.find((x) => x.n === Number(m[1])) : null;
    if (!s) return <span key={i}>{p}</span>;
    return s.kind === "task"
      ? <Link key={i} to="/work/$id" params={{ id: s.id }} className="chip mx-0.5 text-xs text-brand-700">{p}</Link>
      : <a key={i} href={`#note-${s.id}`} className="chip mx-0.5 text-xs text-brand-700">{p}</a>;
  });
  return (
    <Card>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (question.trim().length >= 3) ask.mutate(question.trim()); }}>
        <Sparkles size={18} className="mt-2 shrink-0 text-brand-600" />
        <input className="input flex-1" placeholder="Ask your knowledge… e.g. What did we agree with Kempinski about the rent?" value={question} onChange={(e) => setQuestion(e.target.value)} />
        <button className="btn btn-primary" disabled={ask.isPending || question.trim().length < 3}>{ask.isPending ? "Thinking…" : "Ask"}</button>
      </form>
      <ErrorNote error={ask.error} />
      {a && (
        <div className="mt-3 space-y-2 border-t border-[var(--line)] pt-3 text-sm">
          {a.answer ? <div className="whitespace-pre-wrap leading-relaxed">{render(a.answer)}</div> : <p className="muted">{a.note}</p>}
          {a.sources.length > 0 && (
            <div className="muted text-xs">
              From: {a.sources.filter((s) => s.cited || !a.sources.some((x) => x.cited)).map((s) => (
                s.kind === "task"
                  ? <Link key={s.n} to="/work/$id" params={{ id: s.id }} className="mr-2 hover:underline">[{s.n}] {s.ref} {s.title}</Link>
                  : <a key={s.n} href={`#note-${s.id}`} className="mr-2 hover:underline">[{s.n}] {s.title}</a>
              ))}
            </div>
          )}
          <p className="muted text-xs">Answered from your own notes and tasks only. Check the sources before acting on it.</p>
          {a.fusionDocs?.found && (
            <div className="mt-2 space-y-1.5 rounded-lg bg-black/[0.03] px-3 py-2">
              <p className="section-label">From FusionDocs</p>
              <div className="whitespace-pre-wrap leading-relaxed">{a.fusionDocs.answer}</div>
              <div className="muted text-xs">
                {a.fusionDocs.sources.filter((s) => s.cited !== false).map((s) => (
                  s.url ? <a key={s.n} href={s.url} target="_blank" rel="noreferrer" className="mr-2 hover:underline">[{s.n}] {s.title}</a> : <span key={s.n} className="mr-2">[{s.n}] {s.title}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function KnowledgePage() {
  const [text, setText] = useState("");
  const q = useDebounced(text.trim());
  const [scope, setScope] = useState<"all" | "notes" | "tasks">("all");
  const [filter, setFilter] = useState<Filter>({});
  const [open, setOpen] = useState<string | null>(() => (location.hash.startsWith("#note-") ? location.hash.slice(6) : null));
  const facets = useQuery({ queryKey: ["knowledge-facets"], queryFn: () => api<Facets>("/api/knowledge/facets") });
  const cats = useCategories();
  const params = new URLSearchParams({ scope, ...(q ? { q } : {}), ...(filter.folder !== undefined ? { folder: filter.folder } : {}), ...(filter.tag ? { tag: filter.tag } : {}), ...(filter.company ? { company: filter.company } : {}), ...(filter.team ? { team: filter.team } : {}), ...(filter.category ? { category: filter.category } : {}) });
  const res = useQuery({ queryKey: ["knowledge", params.toString()], queryFn: () => api<{ items: Hit[]; fusionDocs?: FusionDocsResult }>(`/api/knowledge?${params}`) });

  useEffect(() => {
    const onHash = () => location.hash.startsWith("#note-") && setOpen(location.hash.slice(6));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const f = facets.data;
  const active = [
    filter.folder !== undefined && { k: "folder", label: filter.folder || "Unfiled" },
    filter.company && { k: "company", label: filter.company },
    filter.team && { k: "team", label: filter.teamName ?? "Team" },
    filter.tag && { k: "tag", label: `#${filter.tag}` },
    filter.category && { k: "category", label: `Tasks · ${filter.categoryName ?? "category"}` },
  ].filter(Boolean) as Array<{ k: keyof Filter; label: string }>;
  const items = res.data?.items ?? [];
  const fdOn = useFusionDocs();

  return (
    <div className="space-y-5">
      <PageHeader title="Knowledge" subtitle="Everything you've written down — notes, your teams' shared notes and your tasks — in one place to find and read."
        actions={<a href="/notes" className="btn btn-sm"><Pencil size={14} /> Write in Notes</a>} />
      <Ask />
      <div className="grid gap-5 lg:grid-cols-[14rem_minmax(0,1fr)] xl:grid-cols-[14rem_minmax(0,1fr)_minmax(0,1.1fr)]">
        <aside className="space-y-4 text-sm">
          <Card>
            <div className="space-y-4">
              <div>
                <h3 className="section-label mb-1 flex items-center gap-1.5"><Folder size={13} /> Folders</h3>
                <ul className="space-y-0.5">
                  {(f?.folders ?? []).map((x) => (
                    <li key={x.folder}><button className={clsx("flex w-full justify-between rounded px-2 py-1 text-left hover:bg-black/[0.04]", filter.folder === x.folder && "bg-brand-50 font-semibold")} onClick={() => setFilter((c) => ({ ...c, folder: c.folder === x.folder ? undefined : x.folder }))}><span className="truncate">{x.folder || "Unfiled"}</span><span className="muted">{x.n}</span></button></li>
                  ))}
                </ul>
              </div>
              {(f?.companies.length ?? 0) > 0 && (
                <div>
                  <h3 className="section-label mb-1">Companies</h3>
                  <div className="flex flex-wrap gap-1">{f!.companies.map((c) => <button key={c.code} title={c.name} className={clsx("chip text-xs", filter.company === c.code && "!border-brand-400 !bg-brand-50")} onClick={() => setFilter((x) => ({ ...x, company: x.company === c.code ? undefined : c.code }))}>{c.code} · {c.n}</button>)}</div>
                </div>
              )}
              {(f?.teams.length ?? 0) > 0 && (
                <div>
                  <h3 className="section-label mb-1 flex items-center gap-1.5"><Users size={13} /> Team knowledge</h3>
                  <ul className="space-y-0.5">{f!.teams.map((t) => <li key={t.id}><button className={clsx("flex w-full justify-between rounded px-2 py-1 text-left hover:bg-black/[0.04]", filter.team === t.id && "bg-brand-50 font-semibold")} onClick={() => setFilter((x) => ({ ...x, team: x.team === t.id ? undefined : t.id, teamName: t.name }))}><span className="truncate">{t.name}</span><span className="muted">{t.n}</span></button></li>)}</ul>
                </div>
              )}
              {(cats.data?.items.length ?? 0) > 0 && (
                <div>
                  <h3 className="section-label mb-1">Task categories</h3>
                  <div className="flex flex-wrap gap-1">{cats.data!.items.map((c) => <button key={c.id} className={clsx("chip text-xs", filter.category === c.id && "!border-brand-400 !bg-brand-50")} onClick={() => setFilter((x) => ({ ...x, category: x.category === c.id ? undefined : c.id, categoryName: c.name }))}><span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}</button>)}</div>
                </div>
              )}
              {(f?.tags.length ?? 0) > 0 && (
                <div>
                  <h3 className="section-label mb-1 flex items-center gap-1.5"><Tag size={13} /> Tags</h3>
                  <div className="flex flex-wrap gap-1">{f!.tags.map((t) => <button key={t.tag} className={clsx("chip text-xs", filter.tag === t.tag && "!border-brand-400 !bg-brand-50")} onClick={() => setFilter((x) => ({ ...x, tag: x.tag === t.tag ? undefined : t.tag }))}>#{t.tag}</button>)}</div>
                </div>
              )}
            </div>
          </Card>
        </aside>

        <div className={clsx("min-w-0 space-y-3", open && "hidden xl:block")}>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] px-3 py-2 focus-within:border-brand-400">
              <Search size={16} className="muted shrink-0" />
              <input className="min-w-0 flex-1 bg-transparent text-sm outline-none" placeholder="Search notes and tasks…" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search knowledge" />
              {text && <button onClick={() => setText("")} aria-label="Clear"><X size={14} className="muted" /></button>}
            </div>
            <div className="tabs !w-auto" role="tablist">
              {(["all", "notes", "tasks"] as const).map((s) => <button key={s} className="tab" aria-selected={scope === s} onClick={() => setScope(s)}>{s === "all" ? "All" : s === "notes" ? "Notes" : "Tasks"}</button>)}
            </div>
          </div>
          {active.length > 0 && (
            <div className="flex flex-wrap gap-1.5 text-xs">
              {active.map((a) => <button key={a.k} className="chip" onClick={() => setFilter((x) => ({ ...x, [a.k]: undefined }))}>{a.label} <X size={11} /></button>)}
              <button className="muted underline" onClick={() => setFilter({})}>Clear filters</button>
            </div>
          )}
          {!q && scope === "tasks" && <p className="muted text-sm">Type something to search your tasks (closed ones included).</p>}
          {res.isLoading ? <Empty>Searching…</Empty> : items.length === 0 ? (
            <Card><Empty>{q ? `Nothing found for “${q}”.` : "No notes here yet."}</Empty></Card>
          ) : (
            <ul className="space-y-2">
              {items.map((h) => (
                <li key={`${h.kind}-${h.id}`}>
                  {h.kind === "note" ? (
                    <button onClick={() => setOpen(h.id)} className={clsx("w-full rounded-xl border bg-[var(--panel)] p-3 text-left hover:border-brand-300", open === h.id ? "border-brand-400" : "border-[var(--line)]")}>
                      <span className="flex items-center gap-1.5 text-sm font-semibold"><FileText size={14} className="muted shrink-0" /><span className="truncate">{h.title || "Untitled"}</span></span>
                      {h.snippet && <span className="muted mt-1 line-clamp-2 block text-xs"><Snippet text={h.snippet} /></span>}
                      <span className="muted mt-1.5 block truncate text-[0.7rem]">{[h.folder || "Unfiled", h.company, h.team ?? "Private", fmtDate(h.at)].filter(Boolean).join(" · ")}</span>
                    </button>
                  ) : (
                    <Link to="/work/$id" params={{ id: h.id }} className="block rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 hover:border-brand-300">
                      <span className="flex items-center gap-1.5 text-sm font-semibold"><CheckSquare size={14} className="muted shrink-0" /><span className="min-w-0 flex-1 truncate">{h.title}</span><SubProgress s={h.subtasks} /></span>
                      {h.snippet && <span className="muted mt-1 line-clamp-2 block text-xs"><Snippet text={h.snippet} /></span>}
                      <span className="muted mt-1.5 block truncate text-[0.7rem]">{[h.ref, h.company, h.state === "done" ? "Done" : h.state === "cancelled" ? "Closed" : "Open", fmtDate(h.at)].filter(Boolean).join(" · ")}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
          {fdOn && q && !res.isLoading && <FusionDocsHits result={res.data?.fusionDocs} />}
        </div>

        <div className={clsx("min-w-0", !open && "hidden xl:block")}>
          {open ? <Reader key={open} id={open} onOpen={setOpen} onClose={() => { setOpen(null); if (location.hash) history.replaceState(null, "", location.pathname); }} /> : (
            <Card><Empty><BookOpen size={20} className="muted mx-auto mb-2" />Pick a note to read it here.{f ? ` ${f.total} notes.` : ""}</Empty></Card>
          )}
        </div>
      </div>
    </div>
  );
}
